/**
 * The client's VIEW of a detached run: folding one SSE channel into the stream
 * state, and keeping that view alive across a channel that dies under it. Split
 * out of `chat.ts` (500-LOC ceiling) with every dependency injected (DIP), so the
 * "a dropped reader is not a dropped turn" rule is exercised on its own.
 */
import { type ApiError, isErr, type Result } from '@4f/shared';
import { toApiError } from '../api/envelope.ts';
import { NetworkError } from '../api/errors.ts';
import { streamChatEvents } from '../features/chat/sse-reader.ts';
import {
  applyChatEvent,
  markStreamFailed,
  type StreamState,
} from '../features/chat/stream-model.ts';
import { refusalOf } from './chat-delivery.ts';

/** What the run view needs from the store that owns the stream. */
export interface RunViewDeps {
  /** `GET /stream` — the reattach path; `runId` omitted targets the latest run. */
  readonly attach: (
    conversationId: string,
    runId?: string,
    signal?: AbortSignal,
  ) => Promise<Result<Response, ApiError>>;
  readonly setStream: (update: (current: StreamState) => StreamState) => void;
  /** Drop the accumulated view — a reattach replays the run from its start. */
  readonly resetStream: () => void;
  /** The run being tailed, once `run-started` has arrived. */
  readonly currentRunId: () => string | null;
  readonly setError: (error: ApiError) => void;
}

export interface RunView {
  /**
   * Fold one SSE view into the stream until the channel closes. A JSON body is a
   * pre-stream failure, surfaced as an error AND as a failed turn — the view has
   * no run event to learn it from any other way (see `markStreamFailed`).
   */
  consume(response: Response, signal: AbortSignal): Promise<void>;
  /**
   * Tail a run's view to its end, RE-ATTACHING once if the channel dies under it.
   * The run is DETACHED and server-side: a reader that drops is not a failed turn,
   * and marking it failed offers a "tap to retry" that re-sends — and re-bills — a
   * turn that is still being generated.
   *
   * Covers a channel that ERRORS. A channel that closes cleanly with the run still
   * live and no terminal event (a proxy idle-timeout mid-reply) still leaves the
   * turn parked on screen — a separate gap, recorded rather than half-fixed here.
   */
  tail(view: Response, signal: AbortSignal, conversationId: string): Promise<void>;
  /** Surface a dead channel as a failed turn — the last resort, with no run to tail. */
  fail(error: ApiError): void;
}

export function createRunView(deps: RunViewDeps): RunView {
  const fail = (error: ApiError): void => {
    deps.setError(error);
    deps.setStream((current) => markStreamFailed(current, error.message));
  };

  const consume = async (response: Response, signal: AbortSignal): Promise<void> => {
    const refusal = await refusalOf(response);
    if (refusal) {
      fail(refusal.error);
      return;
    }
    const body = response.body;
    if (!body) {
      return;
    }
    try {
      for await (const event of streamChatEvents(body, signal)) {
        deps.setStream((current) => applyChatEvent(current, event));
      }
    } finally {
      // The reader stops at the first read after an abort and releases its lock,
      // which does NOT close the channel — so every companion switch mid-stream
      // left a socket open until the edge gave up on it.
      if (signal.aborted) {
        void body.cancel().catch(() => undefined);
      }
    }
  };

  const tail = async (view: Response, signal: AbortSignal, convId: string): Promise<void> => {
    try {
      await consume(view, signal);
      return;
    } catch (cause) {
      if (signal.aborted) {
        return;
      }
      // `runId` is null when the channel died before `run-started`; the edge then
      // targets the conversation's latest run which — since it serializes them —
      // is ours.
      const again = await deps.attach(convId, deps.currentRunId() ?? undefined, signal);
      if (signal.aborted) {
        if (!isErr(again)) {
          void again.value.body?.cancel().catch(() => undefined);
        }
        return;
      }
      if (isErr(again)) {
        fail(toApiError(new NetworkError(cause)));
        return;
      }
      // `GET /stream` replays the run from its start, so the view is rebuilt rather
      // than continued — otherwise every token before the drop renders twice.
      deps.resetStream();
      try {
        await consume(again.value, signal);
      } catch (secondCause) {
        if (!signal.aborted) {
          fail(toApiError(new NetworkError(secondCause)));
        }
      }
    }
  };

  return { consume, tail, fail };
}
